import React, { useEffect, useCallback } from 'react';
import { UpSellPage } from '../pages/up-sell/ups-sell.page';
import { useApi } from '../../common/hooks/useApi';
import { useLocation, useParams } from 'react-router-dom';
import { getUrlParams } from '../../common/utils/get-url-params';
import { gerarTokenRecaptcha } from '../../common/utils/getReCaptcha';
import { MetodoPagamentoVendaEnum } from '../../common/types/enums/metodo-pagamento.enum';
import { Sell } from '../../common/types/venda';
import { useWebSocket } from '../../common/hooks/useWebSocket';
import { useTranslation } from 'react-i18next';
import { UpSell } from '../../common/types/up-sell';
import { UpSellIntent } from '../../common/types/up-sell-intent';
import { PixUpsellModal } from '../molecules/pix-upsell-modal/pix-upsell-modal';
import { OnAcceptUpSellEnum } from '../../common/types/enums/on-accept-up-sell.enum';
import { navigateExternalNoReferrer } from '../../common/utils/navigate-no-referer';
import { useVendaStatusObserver } from '../../common/hooks/useVendaStatusObserver';
import { GatewayVendaEnum } from '../../common/types/enums/gateway-venda.enum';
import { resolveUpsellOffer } from '../../common/utils/resolve-upsell-offer';
import { StatusVendaEnum } from '../../common/types/enums/status-venda.enum';
import {
  awaitEmitUtmifyEvent,
  emitUtmifyEvent,
} from '../../common/utils/emitUtmifyEvent';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type MpUpsellCreditCardPayload = {
  requestId: string;
  mp: {
    token: string;
    installments: number;
    paymentMethodId?: string | null;
    issuer?: number | null;
  };
};

function waitForMpCreditCardPayload(opts: {
  requestId: string;
  timeoutMs?: number;
}) {
  const timeoutMs = opts.timeoutMs ?? 120_000;

  return new Promise<MpUpsellCreditCardPayload>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Timeout aguardando dados do cartão.'));
    }, timeoutMs);

    const onMessage = (event: MessageEvent) => {
      if (!event.data) return;

      const { source, type, payload } = event.data as any;
      if (source !== 'VendepayUpsell') return;

      // ✅ garante que é a resposta do modal certo
      if (!payload || payload.requestId !== opts.requestId) return;

      if (type === 'CC_TOKEN') {
        cleanup();
        resolve(payload as MpUpsellCreditCardPayload);
        return;
      }

      if (type === 'CC_CLOSED') {
        cleanup();
        reject(Object.assign(new Error('CC_CLOSED'), { code: 'CC_CLOSED' }));
        return;
      }

      if (type === 'CC_ERROR') {
        cleanup();
        reject(new Error(payload?.message ?? 'Erro no checkout do cartão.'));
      }
    };

    const cleanup = () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
    };

    window.addEventListener('message', onMessage);
  });
}

export const UpSellTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const vendaId = getUrlParams('vendaId');
  const api = useApi();
  const { socket } = useWebSocket();
  const location = useLocation();
  const { i18n } = useTranslation();

  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [refused, setRefused] = React.useState<boolean>(false);

  const [upsellIntent, setUpsellIntent] = React.useState<UpSellIntent>(
    {} as UpSellIntent,
  );

  const [sell, setSell] = React.useState<Sell | null>(null);
  const [showPixModal, setShowPixModal] = React.useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
  const resolutionHandledRef = React.useRef(false);

  const effectiveUpSell = React.useMemo<UpSell>(() => {
    const intentUpsell = upsellIntent?.upsell;
    if (!intentUpsell) {
      return { id: '', produtoId: '', produtoOfertaId: '', ordem: 1, precos: [] };
    }
    return intentUpsell;
  }, [upsellIntent]);

  const resolvedOffer = React.useMemo(
    () => resolveUpsellOffer(effectiveUpSell),
    [effectiveUpSell],
  );

  const RETRY_BASE_DELAY_MS = 300;
  const RETRY_MAX_ATTEMPTS = 8;

  const getUpSellIntentWithRetry = async () => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        const { data: _upSellIntent } = await api.UpSell.intent(
          id ?? '',
          vendaId ?? '',
        );

        await i18n.changeLanguage(_upSellIntent?.venda?.checkout?.linguagem);

        setUpsellIntent(_upSellIntent);
        return;
      } catch (e) {
        lastError = e;

        if (attempt < RETRY_MAX_ATTEMPTS) {
          const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          // eslint-disable-next-line no-console
          console.log(
            `[upsell-intent retry] tentativa ${attempt + 1} em ${delay}ms`,
          );
          await sleep(delay);
        }
      }
    }
    throw lastError;
  };

  const getData = async () => {
    try {
      setHasError(false);
      setIsLoading(true);

      await getUpSellIntentWithRetry();
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmUpSell = async () => {
    try {
      setHasError(false);
      setIsLoading(true);
      setIsSuccess(false);
      setIsProcessingPayment(false);
      resolutionHandledRef.current = false;

      const gateway = (upsellIntent as any)?.venda?.gateway;
      const metodo = (upsellIntent as any)?.venda?.metodoPagamento;

      const isMpCreditCard =
        gateway === GatewayVendaEnum.MERCADO_PAGO &&
        metodo === MetodoPagamentoVendaEnum.CARTAO_CREDITO;

      let extraBody: any = undefined;

      if (isMpCreditCard) {
        const requestId =
          (crypto as any)?.randomUUID?.() ?? `req_${Date.now()}`;

        window.parent?.postMessage(
          {
            source: 'VendepayUpsell',
            type: 'CC_OPEN',
            payload: {
              requestId,
              intentId: upsellIntent?.id,
              upsellId: id,
              vendaId,
              amount: resolvedOffer.amount ?? undefined,
            },
          },
          '*',
        );

        try {
          const cc = await waitForMpCreditCardPayload({ requestId });

          extraBody = {
            mp: {
              creditCard: {
                token: cc.mp.token,
                paymentMethodId: cc.mp.paymentMethodId ?? undefined,
                issuer: cc.mp.issuer != null ? Number(cc.mp.issuer) : undefined,
                installments: cc.mp.installments ?? 1,
              },
            },
          };
        } catch (err: any) {
          if (
            err?.code === 'CC_CLOSED' ||
            String(err?.message) === 'CC_CLOSED'
          ) {
            setHasError(false);
            setIsSuccess(false);
            setRefused(false);
            return;
          }

          throw err;
        }
      }

      const recaptcha = await gerarTokenRecaptcha('/up_sell/confirm');

      console.log('extraBody', extraBody);
      const { data: venda } = await api.UpSell.confirm(
        upsellIntent?.id,
        recaptcha,
        extraBody,
      );

      setSell(venda);

      if (venda.metodoPagamento === MetodoPagamentoVendaEnum.PIX) {
        emitUtmifyEvent({
          event: 'waiting_payment',
          vendaId: venda.id,
        });
        setShowPixModal(true);

        window.parent?.postMessage(
          {
            source: 'VendepayUpsell',
            type: 'PIX_OPEN',
            payload: {
              pixQrCode: venda.pixQrCode,
              pixCopiaCola: venda.pixCopiaCola,
              amount: resolvedOffer.amount ?? undefined,
            },
          },
          '*',
        );

        return;
      }

      if (venda.status === StatusVendaEnum.PAID) {
        handleResolvedSuccess(venda);
        return;
      }

      if (
        venda.status === StatusVendaEnum.FAILED ||
        venda.status === StatusVendaEnum.CANCELED ||
        venda.status === StatusVendaEnum.REFUSED
      ) {
        handleResolvedFailed(venda);
        return;
      }

      if (typeof venda.status !== 'number') {
        setIsSuccess(true);
        return;
      }

      emitUtmifyEvent({
        event: 'waiting_payment',
        vendaId: venda.id,
      });
      setIsProcessingPayment(true);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.log(e?.response?.data ?? e);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolvedSuccess = useCallback((resolvedSell?: Sell | null) => {
    if (resolutionHandledRef.current) return;
    resolutionHandledRef.current = true;

    const params = new URLSearchParams(location.search);
    const currentSell = resolvedSell ?? sell;

    setShowPixModal(false);
    setIsProcessingPayment(false);
    setIsSuccess(true);
    setHasError(false);

    window.parent?.postMessage(
      {
        source: 'VendepayUpsell',
        type: 'PIX_SUCCESS',
        payload: {
          vendaId: currentSell?.id,
        },
      },
      '*',
    );

    void (async () => {
      if (currentSell?.id) {
        await awaitEmitUtmifyEvent({
          event: 'paid',
          vendaId: currentSell.id,
        });
      }
      if (
        effectiveUpSell?.precos?.[0]?.onAccept ===
        OnAcceptUpSellEnum.REDIRECIONAR
      ) {
        navigateExternalNoReferrer(effectiveUpSell?.precos?.[0]?.urlAccept ?? '', {
          newTab: false,
          extraParams: params,
        });
      }
    })();
  }, [effectiveUpSell?.precos, location.search, sell]);

  const handleResolvedFailed = useCallback((resolvedSell?: Sell | null) => {
    if (resolutionHandledRef.current) return;
    resolutionHandledRef.current = true;

    const vendaId = resolvedSell?.id ?? sell?.id;
    if (vendaId) {
      emitUtmifyEvent({ event: 'refused', vendaId });
    }

    setShowPixModal(false);
    setIsProcessingPayment(false);
    setIsSuccess(false);
    setHasError(true);
  }, [sell]);

  useVendaStatusObserver({
    socket,
    vendaId: sell?.id ?? undefined,
    vendaToken: sell?.vendaToken ?? undefined,

    wsEventName: 'venda-cc',

    fallbackEnabled: true,
    fallbackStartAfterMs: 6000,
    fallbackOnSocketError: true,
    fallbackOnDisconnect: false,

    pollIntervalMs: 2500,
    pollStopAfterMs: 10 * 60_000,

    resolveFromStatus: (status: number) => {
      if (status === StatusVendaEnum.PAID) return 'success';
      if (
        status === StatusVendaEnum.FAILED ||
        status === StatusVendaEnum.CANCELED ||
        status === StatusVendaEnum.REFUSED
      )
        return 'failed';
      return null;
    },

    onResolved: ({ type }) => {
      if (type === 'success') handleResolvedSuccess();
      if (type === 'failed') handleResolvedFailed();
    },

    debug: false,
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <UpSellPage
        setRefused={setRefused}
        refused={refused}
        onConfirm={confirmUpSell}
        hasError={hasError}
        isProcessing={isProcessingPayment}
        isLoading={isLoading}
        isSuccess={isSuccess}
        upSell={effectiveUpSell}
      />

      <PixUpsellModal
        open={showPixModal && !!sell}
        sell={sell}
        onClose={() => setShowPixModal(false)}
      />
    </>
  );
};
